import React, { useMemo, useState } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { LayoutGridIcon, SearchIcon, FilterIcon, XIcon, PlusIcon, EditIcon, TrashIcon, CheckIcon } from 'lucide-react';
import { MOCK_IMAGES } from '../schemas/mockData';
import type { Domain, Level, AgeBand, EmotionalCategory } from '../schemas/types';
import { SearchInput } from '../components/SearchInput';
/**
 * Taxonomy Page
 * Visual framework grid showing domain/level distribution
 * Includes promotion/prevention classification for emotional categorization
 */
export function Taxonomy() {
  const {
    t
  } = useOrganization();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDomain, setSelectedDomain] = useState<Domain | 'all'>('all');
  const [selectedLevel, setSelectedLevel] = useState<Level | 'all'>('all');
  const [selectedAgeBand, setSelectedAgeBand] = useState<AgeBand | 'all'>('all');
  const [selectedEmotionalCategory, setSelectedEmotionalCategory] = useState<EmotionalCategory | 'all'>('all');
  const [showFilters, setShowFilters] = useState(true);
  const [selectedCell, setSelectedCell] = useState<{
    domain: Domain;
    level: Level;
  } | null>(null);
  const domains: Domain[] = ['Self', 'Material', 'Social', 'Spiritual'];
  const levels: Level[] = ['Foundational', 'Experiential', 'Aspirational'];
  // Filter images based on criteria
  const filteredImages = useMemo(() => {
    return MOCK_IMAGES.filter(image => {
      const matchesSearch = searchQuery === '' || image.filename.toLowerCase().includes(searchQuery.toLowerCase()) || image.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())) || image.id.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesDomain = selectedDomain === 'all' || image.domain === selectedDomain;
      const matchesLevel = selectedLevel === 'all' || image.level === selectedLevel;
      const matchesAgeBand = selectedAgeBand === 'all' || image.ageBands.includes(selectedAgeBand);
      const matchesEmotionalCategory = selectedEmotionalCategory === 'all' || image.emotionalCategory === selectedEmotionalCategory;
      return matchesSearch && matchesDomain && matchesLevel && matchesAgeBand && matchesEmotionalCategory;
    });
  }, [searchQuery, selectedDomain, selectedLevel, selectedAgeBand, selectedEmotionalCategory]);
  // Build grid data
  const gridData = useMemo(() => {
    const grid: {
      [key: string]: {
        [key: string]: typeof filteredImages;
      };
    } = {};
    domains.forEach(domain => {
      grid[domain] = {};
      levels.forEach(level => {
        grid[domain][level] = filteredImages.filter(img => img.domain === domain && img.level === level);
      });
    });
    return grid;
  }, [filteredImages]);
  // Calculate statistics
  const stats = useMemo(() => {
    return {
      total: MOCK_IMAGES.length,
      filtered: filteredImages.length,
      promotion: filteredImages.filter(img => img.emotionalCategory === 'promotion').length,
      prevention: filteredImages.filter(img => img.emotionalCategory === 'prevention').length,
      byDomain: domains.reduce((acc, domain) => {
        acc[domain] = filteredImages.filter(img => img.domain === domain).length;
        return acc;
      }, {} as {
        [key: string]: number;
      }),
      byLevel: levels.reduce((acc, level) => {
        acc[level] = filteredImages.filter(img => img.level === level).length;
        return acc;
      }, {} as {
        [key: string]: number;
      })
    };
  }, [filteredImages]);
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedDomain('all');
    setSelectedLevel('all');
    setSelectedAgeBand('all');
    setSelectedEmotionalCategory('all');
  };
  const hasActiveFilters = searchQuery !== '' || selectedDomain !== 'all' || selectedLevel !== 'all' || selectedAgeBand !== 'all' || selectedEmotionalCategory !== 'all';
  const getDomainColor = (domain: Domain) => {
    const colors = {
      Self: 'from-purple-500 to-purple-600',
      Material: 'from-blue-500 to-blue-600',
      Social: 'from-green-500 to-green-600',
      Spiritual: 'from-orange-500 to-orange-600'
    };
    return colors[domain];
  };
  const getLevelColor = (level: Level) => {
    const colors = {
      Foundational: 'from-emerald-500 to-emerald-600',
      Experiential: 'from-amber-500 to-amber-600',
      Aspirational: 'from-rose-500 to-rose-600'
    };
    return colors[level];
  };
  return <div className="min-h-full bg-gray-50" data-id="element-2775">
      {/* Sticky Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm" data-id="element-2776">
        <div className="max-w-7xl mx-auto px-6 py-4" data-id="element-2777">
          <div className="flex items-center justify-between" data-id="element-2778">
            <div className="flex items-center gap-3" data-id="element-2779">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm" data-id="element-2780">
                <LayoutGridIcon className="w-6 h-6 text-white" data-id="element-2781" />
              </div>
              <div data-id="element-2782">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-2783">
                  Taxonomy Framework
                </h1>
                <p className="text-sm text-gray-600 mt-0.5" data-id="element-2784">
                  Visual framework grid with emotional classification
                </p>
              </div>
            </div>
            <button className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all font-medium shadow-sm hover:shadow flex items-center gap-2" data-id="element-2785">
              <PlusIcon className="w-4 h-4" data-id="element-2786" />
              Add Images
            </button>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 py-6" data-id="element-2787">
        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6" data-id="element-2788">
          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2789">
            <div className="text-xs font-semibold text-blue-700 mb-1" data-id="element-2790">
              Total Images
            </div>
            <div className="text-2xl font-bold text-gray-900" data-id="element-2791">
              {stats.total}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2792">
            <div className="text-xs font-semibold text-green-700 mb-1" data-id="element-2793">
              Promotion
            </div>
            <div className="text-2xl font-bold text-gray-900" data-id="element-2794">
              {stats.promotion}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2795">
            <div className="text-xs font-semibold text-orange-700 mb-1" data-id="element-2796">
              Prevention
            </div>
            <div className="text-2xl font-bold text-gray-900" data-id="element-2797">
              {stats.prevention}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2798">
            <div className="text-xs font-semibold text-purple-700 mb-1" data-id="element-2799">
              Self
            </div>
            <div className="text-2xl font-bold text-gray-900" data-id="element-2800">
              {stats.byDomain.Self}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2801">
            <div className="text-xs font-semibold text-pink-700 mb-1" data-id="element-2802">
              Social
            </div>
            <div className="text-2xl font-bold text-gray-900" data-id="element-2803">
              {stats.byDomain.Social}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2804">
            <div className="text-xs font-semibold text-indigo-700 mb-1" data-id="element-2805">
              Filtered
            </div>
            <div className="text-2xl font-bold text-gray-900" data-id="element-2806">
              {stats.filtered}
            </div>
          </div>
        </div>
        {/* Filters Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6" data-id="element-2807">
          {/* Sticky Filter Header */}
          <div className="sticky top-[73px] z-20 bg-white border-b border-gray-200 rounded-t-lg" data-id="element-2808">
            <div className="px-5 py-3" data-id="element-2809">
              <div className="flex items-center justify-between" data-id="element-2810">
                <div className="flex items-center gap-2" data-id="element-2811">
                  <FilterIcon className="w-4 h-4 text-gray-600" data-id="element-2812" />
                  <h2 className="text-sm font-bold text-gray-900" data-id="element-2813">Filters</h2>
                  {hasActiveFilters && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full" data-id="element-2814">
                      Active
                    </span>}
                </div>
                <div className="flex items-center gap-2" data-id="element-2815">
                  {hasActiveFilters && <button onClick={clearFilters} className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-1 font-medium" data-id="element-2816">
                      <XIcon className="w-3 h-3" data-id="element-2817" />
                      Clear
                    </button>}
                  <button onClick={() => setShowFilters(!showFilters)} className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors font-medium" data-id="element-2818">
                    {showFilters ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
            </div>
            {showFilters && <div className="px-5 py-3 bg-gray-50 border-t border-gray-200" data-id="element-2819">
                {/* Search - NOW USING SHARED COMPONENT */}
                <div className="mb-3" data-id="element-2820">
                  <SearchInput value={searchQuery} onChange={setSearchQuery} placeholder="Search by filename, ID, or tags..." className="h-10" data-id="element-2821" />
                </div>
                {/* Filter Controls */}
                <div className="flex items-center gap-3" data-id="element-2822">
                  <select value={selectedEmotionalCategory} onChange={e => setSelectedEmotionalCategory(e.target.value as EmotionalCategory | 'all')} className="h-10 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-w-[160px]" data-id="element-2823">
                    <option value="all" data-id="element-2824">All Categories</option>
                    <option value="promotion" data-id="element-2825">Promotion</option>
                    <option value="prevention" data-id="element-2826">Prevention</option>
                  </select>
                  <select value={selectedDomain} onChange={e => setSelectedDomain(e.target.value as Domain | 'all')} className="h-10 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-w-[140px]" data-id="element-2827">
                    <option value="all" data-id="element-2828">All Domains</option>
                    <option value="Self" data-id="element-2829">Self</option>
                    <option value="Material" data-id="element-2830">Material</option>
                    <option value="Social" data-id="element-2831">Social</option>
                    <option value="Spiritual" data-id="element-2832">Spiritual</option>
                  </select>
                  <select value={selectedLevel} onChange={e => setSelectedLevel(e.target.value as Level | 'all')} className="h-10 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-w-[140px]" data-id="element-2833">
                    <option value="all" data-id="element-2834">All Levels</option>
                    <option value="Foundational" data-id="element-2835">Foundational</option>
                    <option value="Experiential" data-id="element-2836">Experiential</option>
                    <option value="Aspirational" data-id="element-2837">Aspirational</option>
                  </select>
                  <select value={selectedAgeBand} onChange={e => setSelectedAgeBand(e.target.value as AgeBand | 'all')} className="h-10 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-w-[140px]" data-id="element-2838">
                    <option value="all" data-id="element-2839">All Age Bands</option>
                    <option value="5-9" data-id="element-2840">5-9 years</option>
                    <option value="10-14" data-id="element-2841">10-14 years</option>
                    <option value="14-18" data-id="element-2842">14-18 years</option>
                    <option value="Adults" data-id="element-2843">Adults</option>
                    <option value="Students" data-id="element-2844">Students</option>
                  </select>
                </div>
              </div>}
          </div>
        </div>
        {/* Taxonomy Grid */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2845">
          <h2 className="text-base font-bold text-gray-900 mb-4" data-id="element-2846">
            Framework Grid
          </h2>
          <div className="overflow-x-auto" data-id="element-2847">
            <table className="w-full border-collapse" data-id="element-2848">
              <thead data-id="element-2849">
                <tr data-id="element-2850">
                  <th className="w-32 p-3 border border-gray-200 bg-gray-50 text-left font-bold text-gray-700 text-sm sticky top-0 z-10" data-id="element-2851">
                    Domain / Level
                  </th>
                  {levels.map(level => <th key={level} className="p-3 border border-gray-200 bg-gray-50 sticky top-0 z-10" data-id="element-2852">
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r ${getLevelColor(level)} text-white rounded-lg font-bold text-xs shadow-sm`} data-id="element-2853">
                        {level}
                      </div>
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-2854">
                {domains.map(domain => <tr key={domain} data-id="element-2855">
                    <td className="p-3 border border-gray-200 bg-gray-50" data-id="element-2856">
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r ${getDomainColor(domain)} text-white rounded-lg font-bold text-xs shadow-sm`} data-id="element-2857">
                        {domain}
                      </div>
                    </td>
                    {levels.map(level => {
                  const cellImages = gridData[domain][level];
                  const isSelected = selectedCell?.domain === domain && selectedCell?.level === level;
                  const promotionCount = cellImages.filter(img => img.emotionalCategory === 'promotion').length;
                  const preventionCount = cellImages.filter(img => img.emotionalCategory === 'prevention').length;
                  return <td key={`${domain}-${level}`} className={`p-3 border transition-all cursor-pointer ${isSelected ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/50'}`} onClick={() => setSelectedCell({
                    domain,
                    level
                  })} data-id="element-2858">
                          <div className="text-center" data-id="element-2859">
                            <div className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2860">
                              {cellImages.length}
                            </div>
                            <div className="flex items-center justify-center gap-2 mb-1" data-id="element-2861">
                              <div className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded" data-id="element-2862">
                                +{promotionCount}
                              </div>
                              <div className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-bold rounded" data-id="element-2863">
                                -{preventionCount}
                              </div>
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-2864">
                              {cellImages.length === 1 ? 'image' : 'images'}
                            </div>
                          </div>
                        </td>;
                })}
                  </tr>)}
              </tbody>
            </table>
          </div>
          {/* Selected Cell Details */}
          {selectedCell && <div className="mt-6 p-5 bg-indigo-50 border border-indigo-200 rounded-lg" data-id="element-2865">
              <div className="flex items-center justify-between mb-4" data-id="element-2866">
                <h3 className="text-base font-bold text-gray-900" data-id="element-2867">
                  {selectedCell.domain} - {selectedCell.level}
                </h3>
                <button onClick={() => setSelectedCell(null)} className="p-2 hover:bg-indigo-100 rounded-lg transition-colors" data-id="element-2868">
                  <XIcon className="w-5 h-5 text-gray-600" data-id="element-2869" />
                </button>
              </div>
              <div className="grid grid-cols-4 gap-4" data-id="element-2870">
                {gridData[selectedCell.domain][selectedCell.level].map(image => <div key={image.id} className="relative group rounded-lg overflow-hidden border border-indigo-200 bg-white hover:border-indigo-400 hover:shadow-md transition-all" data-id="element-2871">
                      <div className="aspect-square bg-gray-100 relative" data-id="element-2872">
                        {image.url ? <img src={image.thumbnailUrl || image.url} alt={image.filename} className="w-full h-full object-cover" data-id="element-2873" /> : <div className="w-full h-full flex items-center justify-center" data-id="element-2874">
                            <LayoutGridIcon className="w-8 h-8 text-gray-400" data-id="element-2875" />
                          </div>}
                        <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-bold ${image.emotionalCategory === 'promotion' ? 'bg-green-500 text-white' : 'bg-orange-500 text-white'}`} data-id="element-2876">
                          {image.emotionalCategory === 'promotion' ? '+' : '-'}
                        </div>
                      </div>
                      <div className="p-2" data-id="element-2877">
                        <div className="text-xs font-semibold text-gray-900 truncate" data-id="element-2878">
                          {image.filename}
                        </div>
                      </div>
                    </div>)}
              </div>
            </div>}
        </div>
      </div>
    </div>;
}